import { decryptDSuiteUserData, generatePresignedUrl, getS3Data, getS3File, stringToFile } from "../service/api-actions";
import { getAttachmentFile, getCachedAttachmentKeys, putAttachmentFile } from '../db/at-rest.js';
import { getSessionDEK, stripNul, symDecrypt, symEncrypt } from './dekManager.js';
import { ENVELOPE_VERSION_V3, normalizeVersion, openV3, sealV3 } from './envelopeV3.js';
import { icon } from '../assets/svg/attachment-svg.js';
import { isDecryptedValidData } from '../helper/decryptedDataValidate.js';
import JSZip from "jszip";
import { loadWasm } from 'necjs';
import { reportError, toErrorString } from "../utils/errorLogger.js";


const fileTypes = {
  png: 'image/jpeg',
  gif: 'image/jpeg',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  pdf: 'application/pdf',
  svg: 'image/svg+xml',
  PDF: 'application/pdf',
  csv: 'text/csv',
  zip: 'application/zip',
  rar: 'application/octet-stream',
  xlsx_listing: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv_listing: "application/vnd.ms-excel",
  // `sizeLimit: 15` removed — it was unused, it sat in a MIME-type map where a size has no business being,
  // and it contradicted the real limit. Attachment size is capped at 25MB total at every pick site
  // (ComposeEmail handleFileChange/handleFileDrop, SecureMail, common-alert).
  bytes: 1024
}
const userName = localStorage.getItem('UserName');

/**
 * The attachment cache holds DECRYPTED file bytes, so it is sealed under the dMail DEK at rest — see
 * db/at-rest.js, which owns the row envelope (base64 + symEncrypt), the s3url-keyed de-duplication that
 * used to live here, and the legacy-plaintext migration.
 *
 * Note this applies ONLY to the local cache. encryptText / sendAttachment below are the OUTBOUND path,
 * shared with the recipient: sealing those under our DEK would make sent mail permanently unreadable to
 * everyone it was sent to.
 */
async function fileConvertor(decryptedMsg, fileName, split, fileType) {
  const base64String = decryptedMsg.includes(",")
    ? decryptedMsg.split(",")[1]
    : decryptedMsg;
  const byteString = atob(base64String);
  const byteArray = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }
  const newFile = new File([byteArray], fileName, { type: fileType });
  const Object = { name: fileName, s3url: split[split.length - 1], file: newFile };
  await putAttachmentFile(Object);
  return newFile;
}

export const multipleDecryption = async (encryptedData) => {
  // Each entry is { data, text }: the fetched ciphertext TOGETHER WITH the pending entry it came from.
  // This used to be a bare array of texts positionally zipped back against `pending` further down, which
  // silently broke the moment any one fetch failed — see the loop below.
  const fetched = [];
  // Always null: the organization read that used to sit here could only ever assign null to a variable
  // that was already null, so it decided nothing — it just charged every attachment decrypt a DEK
  // resolve, a symDecrypt and a JSON.parse to compute a value it threw away. The `!userName` gate below
  // is therefore a constant; it is kept as-is because the org-user decrypt path (decryptDSuiteUserData)
  // is still the intended shape here, and nothing else in this function reads it.
  const userName = null;
  const walletAddress = localStorage.getItem("walletAddress");
  if (walletAddress) {
    if (!userName) {
      // Drop anything already cached BEFORE building the bulkDecrypt payload. Re-opening an email used to
      // re-fetch and re-decrypt every attachment it carried — a wallet round-trip for bytes already on
      // disk. Filtering here (rather than after) means an all-cached email never reaches the wallet at all.
      // .keys() reads the s3url index only, so this costs nothing close to loading the cached files.
      const wantedKeys = encryptedData
        .map((data) => data?.attachmentUrl?.split('/')?.at(-1))
        .filter(Boolean);
      const cachedUrls = await getCachedAttachmentKeys(wantedKeys);
      const pending = encryptedData.filter((data) => {
        const key = data?.attachmentUrl?.split('/')?.at(-1);
        return key ? !cachedUrls.has(key) : true;
      });
      if (!pending.length) return;
      // A failure here (or an entry with no usable URL) contributes NOTHING to `fetched`, so `fetched` can
      // be shorter than `pending`. That is exactly why the fetched text carries its own `data` entry
      // instead of being pushed onto a bare parallel array: everything downstream is positional, and a
      // single skipped attachment used to shift every later one by a slot — real bytes written out under
      // the WRONG file name and cached under the WRONG s3url, with no error anywhere. `fetched` is now the
      // ONE list the whole pipeline is indexed by, and each element keeps the identity it belongs to.
      for (const data of pending) {
        try {
          const parts = data?.attachmentUrl?.split('/');
          if (!Array.isArray(parts) || parts.length === 0) continue;
          const res = await generatePresignedUrl((parts.at(-1)), 'txt', 'get');
          if (!res?.url || !res?.path) {
            throw new Error("Failed to get presigned URL.");
          }
          const response = await fetch(res.url);
          if (!response.ok) throw new Error("Failed to fetch file from S3");
          const text = await response.text();
          fetched.push({ data, text });
        } catch (error) {
          console.error("Error processing data:", data, error);
        }
      }
      if (!fetched.length) return;
      const filteredValue = fetched.map(({ text }) => {
        const response = text?.replace(/\u0000+/g, '')?.trim();
        try {
          const convertedValue = JSON.parse(response);
          return { encryptedData: convertedValue.data, version: convertedValue?.version || "v1" };
        } catch (error) {
          const userName = localStorage.getItem('UserName');
          const errorStr = toErrorString(error || error.message);
          reportError({
            endpoint: '-',
            message: `FileName: attachmentHelper.js, Fun: filteredValue, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
          });
          return { encryptedData: response, version: "v1" };
        }
      })
      // A 'v2' blob is sealed with the dMail DEK — that only ever happens for the sender's OWN saved copy
      // (emailHelper.js branches at the call site; recipient-bound attachments are always asymmetric).
      // It must NOT reach ncog_bulkDecrypt: that is an asymmetric unwrap, and handing it symmetric
      // ciphertext fails silently and renders as an empty attachment — the same trap the mail body hit.
      //
      // Decrypted values are spliced back into their ORIGINAL positions, because everything below pairs
      // fetched[i] with response.response[i]; a compacted array would attach the wrong bytes to the wrong
      // file. The wallet's { response: [...] } envelope is rebuilt for the same reason.
      const decrypted = new Array(filteredValue.length).fill(null);
      const asymIndexes = [];
      const asymPayload = [];
      for (let i = 0; i < filteredValue.length; i++) {
        const ver = normalizeVersion(filteredValue[i]?.version);
        if (ver === 'v2') {
          decrypted[i] = await symDecrypt(await getSessionDEK(), filteredValue[i].encryptedData);
        } else if (ver === ENVELOPE_VERSION_V3) {
          // v3 = wrapped content key || sealed file. Only the 2596-char wrapped key goes to the wallet; the
          // file itself is opened locally. Deliberately NOT routed through ncog_bulkDecrypt: that is an
          // asymmetric unwrap of a WHOLE blob, and whether the bulk RPC dispatches per item or applies one
          // version to the batch is not knowable from this tree — a batch that applied v1 to a v3 blob would
          // panic the wallet's Go runtime rather than return an error. One item, one decrypt() call.
          //
          // Caught per item on purpose: a single unopenable attachment must not take the other attachments
          // (or the mail body being rendered around this call) down with it. null here renders as an empty
          // attachment, exactly as a failed bulk entry already does.
          try {
            decrypted[i] = await openV3(filteredValue[i].encryptedData);
          } catch (error) {
            const errorStr = toErrorString(error || error.message);
            reportError({
              endpoint: '-',
              message: `FileName: attachmentHelper.js, Fun: multipleDecryption(v3), ${String(error.message || 'Unhandled error')}`,
              error: errorStr,
              userName: localStorage.getItem('UserName') || '',
            });
            console.error('Error opening v3 attachment:', error);
          }
        } else {
          asymIndexes.push(i);
          asymPayload.push(filteredValue[i]);
        }
      }
      if (asymPayload.length) {
        const bulk = await window.ncogProvider.request({
          method: "ncog_bulkDecrypt",
          params: { userMessage: asymPayload },
        });
        asymIndexes.forEach((target, i) => { decrypted[target] = bulk?.response?.[i]; });
      }
      const response = { response: decrypted };
      // MUST iterate `fetched` — the same list `filteredValue`/`decrypted` were built from, element for
      // element. `data` here is the pending entry that this very blob was downloaded for, carried on the
      // element rather than looked up by position in another array, so a fetch that failed earlier can no
      // longer shift a file's bytes onto its neighbour. Iterating `pending` (let alone the unfiltered
      // `encryptedData`) would reintroduce exactly that.
      await Promise.all(fetched.map(async ({ data }, index) => {
        const split = data.attachmentUrl.split('/');
        const dotSplit = data.fileName.split('.');
        const extension = dotSplit[dotSplit.length - 1];
        const fileType = fileTypes[extension];
        let decryptedMsg = response?.response[index]?.replace(/\u0000+/g, '')?.trim();
        decryptedMsg = isDecryptedValidData(decryptedMsg) ? decryptedMsg : "{}";
        await fileConvertor(decryptedMsg, data.fileName, split, fileType);
      }));
    }
  }
};
export const getFile = async (fileName, attachmentUrl) => {
  const split = attachmentUrl.split('/');
  const cachedFile = await getAttachmentFile(split[split.length - 1]);
  if (cachedFile) {
    // const blobUrl = window.URL.createObjectURL(cachedFile);  if need to file as a blob
    return cachedFile;
    // window.open(blobUrl);
  } else {
    const res = await getS3File(fileName, attachmentUrl);
    const dotSplit = fileName.split('.');
    const extenstion = dotSplit[dotSplit.length - 1];
    const fileType = fileTypes[extenstion];
    const walletAddress = localStorage.getItem("walletAddress");
    const encryptedMsg = res.data;
    if (walletAddress) {
      // Always null — see multipleDecryption: the organization read here only ever re-assigned null.
      const userName = null;
      let decryptedMsg = null
      // Dispatch on the version marker that came back with the blob, ALWAYS, before any wallet call. The
      // wallet must never be asked to work out which format it was handed: a legacy asymmetric unwrap of a
      // v3 blob panics the Go runtime instead of returning an error.
      const attachmentVersion = normalizeVersion(res?.version);
      if (userName) {
        decryptedMsg = await decryptDSuiteUserData(encryptedMsg, true);
        decryptedMsg = decryptedMsg?.data
      } else if (attachmentVersion === 'v2') {
        // v2 = sealed with the dMail DEK (our own saved copy): one wallet unwrap per session, then a local
        // symmetric decrypt. Without this branch the blob would go to ncog_decrypt — an asymmetric unwrap
        // against symmetric ciphertext — and the attachment would silently come back empty.
        const plain = stripNul(await symDecrypt(await getSessionDEK(), encryptedMsg));
        decryptedMsg = isDecryptedValidData(plain) ? plain : "{}";
      } else if (attachmentVersion === ENVELOPE_VERSION_V3) {
        // v3 = wrapped content key || sealed file. Only the 2596-char wrapped key reaches the wallet.
        const plain = await openV3(encryptedMsg);
        decryptedMsg = isDecryptedValidData(plain) ? plain : "{}";
      } else {
        // Legacy v1 (and every unrecognised/absent marker): the whole blob goes to the wallet, tagged with
        // the CRYPTO version 'v1'. res.version was never forwarded here in practice — it is `undefined` for
        // every v1 row — so this is the behaviour that has always run, now stated explicitly.
        const response = await window.ncogProvider.request({ method: 'ncog_decrypt', params: { cypherText: encryptedMsg, version: "v1" } });
        decryptedMsg = isDecryptedValidData(response?.response?.replace(/\u0000+/g, '')?.trim()) ? response?.response?.replace(/\u0000+/g, '')?.trim() : "{}";
      }
      // DECODE LOCALLY. This used to POST `decryptedMsg` — the fully DECRYPTED file — to
      // `${BASE_URL}/stringToFile` and use the server's response as the file bytes. That handed the
      // plaintext of every opened attachment to the backend, defeating end-to-end encryption for web
      // attachments entirely, and it was never necessary: fileConvertor() above does the same base64 →
      // File conversion in the browser, and the bulk path already used it.
      //
      // The server round-trip is kept ONLY as a fallback for payloads the local decoder cannot parse
      // (older rows whose base64/mime the backend used to normalise). Nothing new can reach it.
      try {
        return await fileConvertor(decryptedMsg, fileName, split, fileType);
      } catch (localDecodeError) {
        console.warn('Local attachment decode failed; falling back to the server decoder for this legacy attachment:', localDecodeError?.message || localDecodeError);
      }

      const blob = new Blob([decryptedMsg], { type: 'text/plain' });
      const file = new File([blob], 'textfile.txt', { type: 'text/plain' });
      const response = await stringToFile(file, fileName);

      let newBlob = new Blob([response.data], { type: fileType });
      const newFile = new File([newBlob], fileName, { type: fileType });
      const Object = { name: fileName, s3url: split[split.length - 1], file: newFile };
      await putAttachmentFile(Object);
      return newFile;
      // window.open(blobUrl);
    }
  }
};

export const downloadFile = async (fileName, attachmentUrl) => {
  try {

    const attachmentKey = attachmentUrl.split('/').pop();
    const cachedFile = await getAttachmentFile(attachmentKey);
    if (cachedFile) {
      const blobUrl = window.URL.createObjectURL(cachedFile);
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(blobUrl);
      return true;
    }
    const response = await getS3File(fileName, attachmentUrl);
    const encryptedMessage = response?.data;
    const version = response?.version || 'v1';
    const walletAddress = localStorage.getItem("walletAddress");
    if (walletAddress) {
      // Always null — see multipleDecryption: the organization read here only ever re-assigned null.
      const userName = null;
      let decryptedMessage = null
      // Same three-way dispatch as getFile, on the marker that came back with the blob.
      const attachmentVersion = normalizeVersion(version);
      if (userName) {
        decryptedMessage = await decryptDSuiteUserData(encryptedMessage, true);
        decryptedMessage = decryptedMessage?.data;
      } else if (attachmentVersion === 'v2') {
        // v2 = sealed with the dMail DEK (our own saved copy) — symDecrypt locally rather than asking the
        // wallet for an asymmetric unwrap it cannot do. See the same branch in getFile above.
        const plain = stripNul(await symDecrypt(await getSessionDEK(), encryptedMessage));
        decryptedMessage = isDecryptedValidData(plain) ? plain : "{}";
      } else if (attachmentVersion === ENVELOPE_VERSION_V3) {
        // v3 = wrapped content key || sealed file. Only the 2596-char wrapped key reaches the wallet.
        const plain = await openV3(encryptedMessage);
        decryptedMessage = isDecryptedValidData(plain) ? plain : "{}";
      } else {
        // Legacy v1 and every unrecognised/absent marker. 'v1' is the CRYPTO version and is the only value
        // the wallet accepts here; `version` was already defaulted to 'v1' above for every real v1 row.
        const response = await window.ncogProvider.request({ method: 'ncog_decrypt', params: { cypherText: encryptedMessage, version: "v1" } });
        if (!response?.response) {
          return false
        }
        decryptedMessage = isDecryptedValidData(response?.response?.replace(/\u0000+/g, '')?.trim()) ? response?.response?.replace(/\u0000+/g, '')?.trim() : "{}";
      }
      // Same fix as getFile(): build the download locally instead of POSTing the DECRYPTED file to
      // `${BASE_URL}/stringToFile` and downloading the server's echo of it. Downloading an attachment
      // used to upload its plaintext first.
      let downloadUrl;
      try {
        const base64String = decryptedMessage.includes(',') ? decryptedMessage.split(',')[1] : decryptedMessage;
        const byteString = atob(base64String);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
          byteArray[i] = byteString.charCodeAt(i);
        }
        // Generic type on purpose: downloadFile() only receives (fileName, attachmentUrl), and the
        // `download` attribute below carries the real filename, which is what drives the extension and
        // the app association once it is saved.
        downloadUrl = window.URL.createObjectURL(new Blob([byteArray], { type: 'application/octet-stream' }));
      } catch (localDecodeError) {
        console.warn('Local attachment decode failed; falling back to the server decoder for this legacy attachment:', localDecodeError?.message || localDecodeError);
        const blob = new Blob([decryptedMessage], { type: 'text/plain' });
        const file = new File([blob], 'decryptedFile.txt', { type: 'text/plain' });
        const uploadResponse = await stringToFile(file, fileName);
        downloadUrl = window.URL.createObjectURL(new Blob([uploadResponse.data], { type: uploadResponse.headers['content-type'] }));
      }
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.setAttribute('download', fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(downloadUrl);
    }
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: attachmentHelper.js, Fun: downloadFile, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userId: userName || '',
    });
    console.error('Error downloading file:', error);
  }
}

export const getFileIcon = async (fileType) => {
  const dotSplit = fileType.split('.');
  const extenstion = dotSplit[dotSplit.length - 1].toLowerCase();
  switch (extenstion) {
    case 'pdf':
      return icon.pdf_svg;
    case 'jpeg':
    case 'jpg':
      return icon.jpg_svg;
    case 'png':
      return icon.png_svg;
    case 'gif':
      return icon.gif_svg;
    case 'docx':
      return icon.doc_svg;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return icon.xls_svg;
    case 'svg':
      return icon.svg;
    case 'txt':
      return icon.text_svg;
    default:
      return icon.default_file_svg;
  }
}

export async function convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * encryptText is the OUTBOUND asymmetric primitive and is SHARED: emailHelper.js calls it both for the
 * sender's own copy and — via getEncryptedMessageByUser — for every RECIPIENT, against the recipient's
 * public key. It must stay exactly as it is. Sealing here under our DEK would make every mail we send
 * permanently unreadable to the people we send it to, with no way back. The DEK cutover therefore branches
 * at the CALL SITE (emailHelper.js) and uses encryptFilesWithDek below; it never reaches into this
 * function or into sendAttachment/encryptFiles.
 */
export async function encryptText(msg, publicKey) {
  try {
    const Mlkem = await loadWasm();
    const encryptedTextFile = await Mlkem.encrypt(publicKey, msg);
    return encryptedTextFile;
  }
  catch (error) {
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: attachmentHelper.js, Fun: encryptText, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error('Error encrypting text:', error);
    return null;
  }
}

async function generateEncryptedTextFile(fileBase64, publicKey, originalFileName, fileName) {
  try {
    const encryptedTextFile = await encryptText(fileBase64, publicKey);
    const fileObject = {
      name: originalFileName,
      version: encryptedTextFile?.version,
      data: encryptedTextFile?.encryptedData,
    };
    const jsonString = JSON.stringify(fileObject, null, 2);
    const blob = new Blob([jsonString], { type: "text/plain" });
    const file = new File([blob], fileName, { type: "text/plain" });
    return file;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: attachmentHelper.js, Fun: generateEncryptedTextFile, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error('Error generating encrypted text file:', error);
    return null;
  }

}

/**
 * Thrown when one or more attachments could not be encrypted/uploaded.
 *
 * This exists so the send path can tell an attachment failure apart from any other error and name the exact
 * files to the user. It MUST propagate: see the note on encryptFiles below.
 */
export class AttachmentUploadError extends Error {
  constructor(failedFiles) {
    super(`Attachment upload failed: ${failedFiles.join(", ")}`);
    this.name = "AttachmentUploadError";
    this.failedFiles = failedFiles;
  }
}

/** Split Promise.allSettled results into the uploaded paths and the names of the files that failed. */
function partitionUploadResults(results, files) {
  const returnValue = [];
  const failedFiles = [];
  results.forEach((r, index) => {
    if (r.status !== "fulfilled" || r.value === null || r.value?.__attachmentUploadFailed) {
      failedFiles.push(r.value?.__attachmentUploadFailed || files[index]?.name || "unknown_file");
      return;
    }
    returnValue.push(r.value);
  });
  return { returnValue, failedFiles };
}

export async function encryptFiles(files, publicKey) {
  if (!Array.isArray(files) || files.length === 0) {
    console.warn("No files provided for encryption/upload.");
    return { returnValue: [] };
  }

  const results = await Promise.allSettled(
    files.map(async (file) => {
      try {
        if (!file) throw new Error("Invalid file.");

        const fileName = (file.name || "unknown_file");
        const baseName = encodeURIComponent(fileName.split(".")[0]);

        const expirySeconds = 30 * 60; // 30 mins
        const futureTimestamp = Math.floor(Date.now() / 1000) + expirySeconds;
        const fileNameWithTimestamp = `${futureTimestamp}_${baseName}`;

        //convert file to base64
        const fileBase64 = await convertFileToBase64(file);
        const encryptedTextFile = await generateEncryptedTextFile(fileBase64, publicKey, file?.name, fileNameWithTimestamp);

        // Request presigned URL
        const res = await generatePresignedUrl(fileNameWithTimestamp);
        if (!res?.url || !res?.path) {
          throw new Error("Failed to get presigned URL.");
        }

        // Upload file to S3
        const uploadRes = await fetch(res.url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: encryptedTextFile,
        });

        if (!uploadRes.ok) {
          throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
        }

        return { [fileName]: res.path };

      } catch (err) {
        const errorStr = toErrorString(err || err.message);
        reportError({
          endpoint: '-',
          message: `FileName: attachmentHelper.js, Fun: results, ${String(err.message || 'Unhandled error')}`,
          error: errorStr,
          userName: userName || '',
        });
        console.error(`Error uploading "${file?.name}":`, err);
        // Carry the file name out with the failure — the caller has to be able to tell the user WHICH
        // attachment did not make it.
        return { __attachmentUploadFailed: file?.name || "unknown_file" };
      }
    })
  );

  const { returnValue, failedFiles } = partitionUploadResults(results, files);

  // Do NOT return just the survivors. This used to silently drop every failed upload, so the mail went out
  // with only the attachments that happened to succeed while the sender was still shown "Message Sent" —
  // they had no way to know files were missing until the recipient asked. Throwing blocks the send instead;
  // the send path catches this specific error and names the files. Any new caller MUST let it propagate.
  if (failedFiles.length) {
    throw new AttachmentUploadError(failedFiles);
  }

  return { returnValue };
}

/**
 * Read every attachment to base64 ONCE for a whole send.
 *
 * The recipient path runs per recipient, so a 3-recipient mail used to run convertFileToBase64 three times
 * over the same immutable File — three FileReader passes and three ~1.33x-sized strings per attachment, for
 * bytes that are identical every time. The v3 send hoists that here and hands the result down.
 *
 * The base64 CIPHERTEXT still cannot be shared between recipients — see encryptFilesV3 — but the plaintext
 * read genuinely can be, and it is the expensive half for large files.
 *
 * Returns a Map keyed by the File object. Scoped to one send on purpose (not a module-level cache): it holds
 * attachment plaintext, so it must die with the send rather than linger for the session.
 */
export async function readFilesAsBase64(files) {
  const byFile = new Map();
  if (!Array.isArray(files)) return byFile;
  await Promise.all(
    files.filter(Boolean).map(async (file) => {
      byFile.set(file, await convertFileToBase64(file));
    })
  );
  return byFile;
}

/**
 * v3 upload — the RECIPIENT path under the v3 envelope.
 *
 * Wire format is identical in shape to generateEncryptedTextFile ({ name, version, data }); only `data`
 * changes, and it is built EXACTLY as the body blob is:
 *
 *     data = <2596-char wrapped K> + symSeal(K, fileBase64)
 *
 * The wrapped K is passed in, not derived here, so that one recipient's body and every one of that
 * recipient's attachments carry the byte-identical prefix from a single KEM operation.
 *
 * There is deliberately NO separate `key` field. multipleDecryption collapses every parsed attachment to
 * { encryptedData: json.data, version: json.version } — a sibling field is discarded at that line, and every
 * downstream reader would get a payload with no wrapped key at all. Keeping the concatenation means `data`
 * stays one opaque string and all three read sites needed only a version branch.
 *
 * `fileBase64` is produced by convertFileToBase64 exactly as the v1 path produces it (a FileReader data URL,
 * so it carries the `data:…;base64,` prefix). v3 does not change that and must not try to: readers already
 * split on "," (fileConvertor), and altering it here would break both v1 and v3 at once.
 *
 * ONE UPLOAD PER RECIPIENT IS UNAVOIDABLE, and not for want of trying: `data` carries a wrapped K that is
 * bound to ONE recipient's public key, so two recipients cannot share one S3 object. Sharing K across
 * recipients would not help either — the 2596-char prefix would still only open for whoever it was wrapped
 * to — and it would let any recipient open the copy addressed to any other, which leaks the BCC list. The
 * per-recipient upload cost is therefore identical to v1, i.e. unchanged, not a regression. What v3 removes
 * is the wallet-path cost, which is where the 261x win is. `base64ByFile` (see readFilesAsBase64) removes
 * the one piece of per-recipient work that genuinely was redundant: re-reading the same File.
 *
 * Callers must be gated on isV3WriteEnabled(). Throws rather than falling back to v1 — a v3 blob is not
 * openable by a v1 reader and vice versa, so a silent downgrade is unrecoverable data loss.
 */
export async function encryptFilesV3(files, wrappedK, kHex, base64ByFile = null) {
  if (!Array.isArray(files) || files.length === 0) {
    console.warn("No files provided for encryption/upload.");
    return { returnValue: [] };
  }

  const results = await Promise.allSettled(
    files.map(async (file) => {
      try {
        if (!file) throw new Error("Invalid file.");

        const fileName = (file.name || "unknown_file");
        const baseName = encodeURIComponent(fileName.split(".")[0]);

        const expirySeconds = 30 * 60; // 30 mins
        const futureTimestamp = Math.floor(Date.now() / 1000) + expirySeconds;
        const fileNameWithTimestamp = `${futureTimestamp}_${baseName}`;

        // Reuse the send-scoped read when the caller supplied one; fall back to reading it here so this
        // function is still correct when called on its own.
        const fileBase64 = base64ByFile?.get(file) ?? await convertFileToBase64(file);
        const fileObject = {
          name: fileName,
          // The literal envelope tag. NEVER the crypto layer's version — symEncrypt always reports 'v1',
          // and forwarding that would write 'v1' onto a v3 blob, making the attachment unopenable forever.
          version: ENVELOPE_VERSION_V3,
          data: await sealV3(wrappedK, kHex, fileBase64),
        };
        const blob = new Blob([JSON.stringify(fileObject, null, 2)], { type: "text/plain" });
        const encryptedTextFile = new File([blob], fileNameWithTimestamp, { type: "text/plain" });

        const res = await generatePresignedUrl(fileNameWithTimestamp);
        if (!res?.url || !res?.path) {
          throw new Error("Failed to get presigned URL.");
        }

        const uploadRes = await fetch(res.url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: encryptedTextFile,
        });

        if (!uploadRes.ok) {
          throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
        }

        return { [fileName]: res.path };

      } catch (err) {
        const errorStr = toErrorString(err || err.message);
        reportError({
          endpoint: '-',
          message: `FileName: attachmentHelper.js, Fun: encryptFilesV3, ${String(err.message || 'Unhandled error')}`,
          error: errorStr,
          userName: userName || '',
        });
        console.error(`Error uploading "${file?.name}":`, err);
        return { __attachmentUploadFailed: file?.name || "unknown_file" };
      }
    })
  );

  const { returnValue, failedFiles } = partitionUploadResults(results, files);

  // Same contract as encryptFiles: never quietly hand back the survivors. Under v3 this also covers a
  // crypto failure, which is exactly the case that must abort the send rather than produce a half-sealed
  // mail — the caller turns this into a named-files error and nothing is written on-chain.
  if (failedFiles.length) {
    throw new AttachmentUploadError(failedFiles);
  }

  return { returnValue };
}

/**
 * DEK-sealed upload — for the sender's OWN saved copy of an attachment, and NOTHING else.
 *
 * Deliberately a separate function rather than a mode flag on encryptFiles/encryptText: those are shared
 * with the recipient path, and the whole point is that a recipient-bound file cannot reach this code by
 * any argument. The sender's own copy is single-owner data — nobody else ever opens it — so it can be
 * sealed symmetrically, which costs one wallet unwrap per session instead of an asymmetric op per file.
 *
 * Wire format matches generateEncryptedTextFile ({ name, version, data }) so the S3 side is unchanged; the
 * blob is tagged version 'v2', which is what tells multipleDecryption / getFile / downloadFile to
 * symDecrypt instead of asking the wallet. ('v2' discriminates the BLOB — the WASM is always given 'v1'.)
 *
 * Callers must be gated on isDekWriteEnabled(). Throws if the DEK cannot be resolved, rather than falling
 * back to an unsealed upload.
 */
export async function encryptFilesWithDek(files) {
  if (!Array.isArray(files) || files.length === 0) {
    console.warn("No files provided for encryption/upload.");
    return { returnValue: [] };
  }

  const dek = await getSessionDEK();

  const results = await Promise.allSettled(
    files.map(async (file) => {
      try {
        if (!file) throw new Error("Invalid file.");

        const fileName = (file.name || "unknown_file");
        const baseName = encodeURIComponent(fileName.split(".")[0]);

        const expirySeconds = 30 * 60; // 30 mins
        const futureTimestamp = Math.floor(Date.now() / 1000) + expirySeconds;
        const fileNameWithTimestamp = `${futureTimestamp}_${baseName}`;

        const fileBase64 = await convertFileToBase64(file);
        const fileObject = {
          name: fileName,
          version: 'v2',
          data: await symEncrypt(dek, fileBase64),
        };
        const blob = new Blob([JSON.stringify(fileObject, null, 2)], { type: "text/plain" });
        const encryptedTextFile = new File([blob], fileNameWithTimestamp, { type: "text/plain" });

        const res = await generatePresignedUrl(fileNameWithTimestamp);
        if (!res?.url || !res?.path) {
          throw new Error("Failed to get presigned URL.");
        }

        const uploadRes = await fetch(res.url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: encryptedTextFile,
        });

        if (!uploadRes.ok) {
          throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
        }

        return { [fileName]: res.path };

      } catch (err) {
        const errorStr = toErrorString(err || err.message);
        reportError({
          endpoint: '-',
          message: `FileName: attachmentHelper.js, Fun: encryptFilesWithDek, ${String(err.message || 'Unhandled error')}`,
          error: errorStr,
          userName: userName || '',
        });
        console.error(`Error uploading "${file?.name}":`, err);
        return { __attachmentUploadFailed: file?.name || "unknown_file" };
      }
    })
  );

  const { returnValue, failedFiles } = partitionUploadResults(results, files);

  // Same contract as encryptFiles: never quietly hand back the survivors. This is the sender's own saved
  // copy, so a silent drop here means the sender's archived mail is missing files it appears to contain.
  if (failedFiles.length) {
    throw new AttachmentUploadError(failedFiles);
  }

  return { returnValue };
}